class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    this.style.removeProperty('visibility');
    this.style.removeProperty('pointer-events');
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    if (!this.classList.contains('active')) return;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    const inner = this.querySelector('.drawer__inner');
    const finalize = () => {
      this.classList.remove('animate');
      if (!this.classList.contains('active')) {
        this.style.visibility = 'hidden';
        this.style.pointerEvents = 'none';
      }
    };

    if (!inner) {
      finalize();
      return;
    }

    let finished = false;
    const onTransitionEnd = (event) => {
      if (event.target !== inner || event.propertyName !== 'transform') return;
      if (finished) return;
      finished = true;
      inner.removeEventListener('transitionend', onTransitionEnd);
      finalize();
    };

    inner.addEventListener('transitionend', onTransitionEnd);
    window.setTimeout(() => {
      if (finished) return;
      finished = true;
      inner.removeEventListener('transitionend', onTransitionEnd);
      finalize();
    }, 500);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  releaseFormTools(root) {
    root?.querySelectorAll?.('form[toolname]')?.forEach((form) => {
      form.removeAttribute('toolname');
      form.removeAttribute('tooldescription');
    });
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    const sections = parsedState.sections || {};
    let paintedDrawer = false;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const html = sections[section.id];
      if (!html) return;
      const inner = this.getSectionInnerHTML(html, section.selector);
      if (inner == null) return;
      this.releaseFormTools(sectionElement);
      sectionElement.innerHTML = inner;
      if (section.id === 'cart-drawer') paintedDrawer = true;
    });

    const finish = () => {
      this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    };

    const stillEmpty =
      this.querySelector('.drawer__inner-empty') && !this.querySelector('.cart-item');

    if (paintedDrawer && !stillEmpty) {
      setTimeout(finish);
      return;
    }

    this.refreshFromCart().finally(finish);
  }

  refreshFromCart() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => this.refreshFromSection(responseText))
      .catch((e) => console.error(e));
  }

  refreshFromSection(html) {
    const parsed = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html;
    const newInner = parsed.querySelector('.drawer__inner');
    const currentInner = this.querySelector('.drawer__inner');
    this.releaseFormTools(newInner);
    this.releaseFormTools(currentInner);
    if (currentInner && newInner) currentInner.replaceWith(newInner);
    const items = this.querySelector('cart-drawer-items');
    this.classList.toggle('is-empty', !items || items.classList.contains('is-empty'));
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!el) return null;
    this.releaseFormTools(el);
    return el.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
